import React, { useEffect, useState } from 'react';
import { Form, Input, Checkbox, Radio, Button, message, Select, Steps } from 'antd';
import { SendOutlined, ArrowRightOutlined, ArrowLeftOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from 'react-query';
import { css, keyframes } from '@emotion/css';
import AxiosInstance from '@/util/Api';

const { TextArea } = Input;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const DecisionForm = () => {
    const [form] = Form.useForm();
    const [activeOtherInput, setActiveOtherInput] = useState(false);
    const [otherInputData, setOtherInputData] = useState('');
    const [currentStep, setCurrentStep] = useState(-1);
    const [showForm, setShowForm] = useState(false);

    useEffect(() => {
        setOtherInputData('')
    }, [activeOtherInput])

    const { isLoading: countryLoading, data: countryData } = useQuery(
        ['/sys/countries'],
        () => AxiosInstance.get('/sys/countries').then((res) => res.data.data),
        { refetchOnMount: false, cacheTime: Infinity, staleTime: Infinity }
    );

    const onFinishFailed = (errorInfo) => {
        console.log('Failed:', errorInfo);
        message.error('Please fill in all required fields correctly.');
    };

    const createDecisionForm = async (values) => {
        const { data: response } = await AxiosInstance.post('/member/adddecisionmaking', values);
        return response;
    };

    const { mutate: createMutate, isLoading } = useMutation(createDecisionForm, {
        onSuccess: (data) => {
            if (data.status === 1) {
                message.success('Successfully added decision');
                form.resetFields();
                setCurrentStep(3);
                setTimeout(() => setShowForm(false), 500);
            } else {
                message.error(data.msg);
            }
        },
        onError: () => {
            message.error('An error occurred. Please try again.');
        },
    });

    const onFinish = () => {
        const data = form.getFieldsValue(true);
        createMutate(data)
    };

    const nextStep = () => {
        const fields = [];
        switch (currentStep) {
            case 0:
                fields.push('full_name', 'email', 'phone', 'country_id');
                break;
            case 1:
                fields.push('decision_today');
                break;
            case 2:
                fields.push('hear_about', 'discipleship');
                break;
            default:
                break;
        }

        form.validateFields(fields)
            .then(() => {
                setCurrentStep(currentStep + 1);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            })
            .catch((err) => {
                console.log('Validation failed:', err);
            });
    };

    const prevStep = () => {
        setCurrentStep(currentStep - 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const valueChangeHandler = (val) => {
        const hasOther = val?.decision_today?.includes('other') ||
            val?.decision_today?.some(v => v === otherInputData);
        setActiveOtherInput(!!hasOther);
    };

    const startForm = () => {
        setCurrentStep(0);
        setShowForm(true);
    };

    const steps = [
        {
            title: 'Personal Information',
            content: (
                <div className={css`animation: ${fadeIn} 0.5s ease;`}>
                    <div style={{
                        marginBottom: '25px',
                        padding: '15px',
                        borderRadius: '8px',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                    }}>
                        <InfoCircleOutlined style={{
                            color: '#008ED0',
                            fontSize: '18px',
                            marginTop: '2px'
                        }} />
                        <div style={{
                            fontSize: '14px',
                            color: '#4a5568',
                            lineHeight: '1.6'
                        }}>
                            <strong style={{ color: '#2d3748' }}>Privacy Notice:</strong> Your details are safe with us.
                            This form only collects information you explicitly provide - we don't automatically
                            gather any personal data without your consent.
                        </div>
                    </div>

                    <Form.Item
                        name="full_name"
                        label={<span style={{ fontWeight: '500', color: '#333' }}>1. Full Name</span>}
                        rules={[{ required: true, message: 'Please enter your full name' }]}
                    >
                        <Input placeholder="Enter your full name" />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label={<span style={{ fontWeight: '500', color: '#333' }}>2. Email Address</span>}
                        rules={[
                            { required: true, message: 'Please enter your email address' },
                            { type: 'email', message: 'Please enter a valid email address' }
                        ]}
                    >
                        <Input placeholder="Enter your email" />
                    </Form.Item>

                    <Form.Item
                        name="phone"
                        label={<span style={{ fontWeight: '500', color: '#333' }}>3. Phone Number (with country code)</span>}
                        rules={[{ required: true, message: 'Please enter your phone number' }]}
                    >
                        <Input placeholder="Enter your phone" />
                    </Form.Item>

                    <Form.Item
                        name="country_id"
                        label={<span style={{ fontWeight: '500', color: '#333' }}>4. Country of Residence</span>}
                        rules={[{ required: true, message: 'Please enter your country of residence' }]}
                    >
                        <Select
                            options={countryData?.map((itm) => ({
                                ...itm,
                                value: itm?.id,
                                label: itm?.name,
                            }))}
                            loading={countryLoading}
                            showArrow
                            showSearch
                            placeholder="Country of Residence"
                            style={{ width: '100%' }}
                            filterOption={(input, option) =>
                                option?.label?.toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Form.Item>
                </div>
            ),
        },
        {
            title: 'Decision Details',
            content: (
                <div className={css`animation: ${fadeIn} 0.5s ease;`}>
                    <Form.Item
                        name="decision_today"
                        label={<span style={{ fontWeight: '500', color: '#333' }}>5. What decision are you making today? (Please select at most 3 options.)</span>}
                        rules={[
                            {
                                required: true,
                                validator: (_, value) => {
                                    if (!value || value.length < 3) {
                                        return Promise.reject('Please select at least 3 options');
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <Checkbox.Group
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                flexDirection: 'column',
                                rowGap: '12px',
                            }}
                        >
                            <Checkbox value="I want to accept Jesus Christ as my Lord and Saviour." style={{ marginLeft: 0 }}>
                                I want to accept Jesus Christ as my Lord and Saviour.
                            </Checkbox>
                            <Checkbox value="I want to rededicate my life to Christ." style={{ marginLeft: 0 }}>
                                I want to rededicate my life to Christ.
                            </Checkbox>
                            <Checkbox value="I want to join a virtual discipleship class." style={{ marginLeft: 0 }}>
                                I want to join a virtual discipleship class.
                            </Checkbox>
                            <Checkbox value="I need prayers." style={{ marginLeft: 0 }}>I need prayers.</Checkbox>
                            <Checkbox value="I want to speak with a Pastor or Counsellor." style={{ marginLeft: 0 }}>
                                I want to speak with a Pastor or Counsellor.
                            </Checkbox>

                            <Checkbox
                                value={otherInputData || 'other'}
                                style={{ marginLeft: 0, width: '100%', display: 'flex' }}
                                className={css`
                                    width: 100%;
                                    display: flex;

                                    span:nth-child(2) {
                                        flex-grow: 1;
                                    }
                                    `}
                            >
                                <Input
                                    placeholder="Write here"
                                    disabled={!activeOtherInput}
                                    value={otherInputData}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setOtherInputData(val);

                                        const current = form.getFieldValue('decision_today') || [];

                                        const filtered = current.filter(
                                            (item) => item !== 'other' && item !== otherInputData
                                        );

                                        if (val) {
                                            form.setFieldValue('decision_today', [...filtered, val]);
                                        } else {
                                            form.setFieldValue('decision_today', [...filtered, 'other']);
                                        }
                                    }}
                                    style={{ flexGrow: 1 }}
                                />
                            </Checkbox>
                        </Checkbox.Group>
                    </Form.Item>
                </div>
            ),
        },
        {
            title: 'Additional Information',
            content: (
                <div className={css`animation: ${fadeIn} 0.5s ease;`}>
                    <Form.Item
                        name="hear_about"
                        label={<span style={{ fontWeight: '500', color: '#333' }}>6. How did you hear about this Outreach? (Please select 2 options.)</span>}
                        rules={[
                            {
                                required: true,
                                validator: (_, value) => {
                                    if (!value || value.length < 2) {
                                        return Promise.reject('Please select at least 2 options');
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <Checkbox.Group
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                flexDirection: 'column',
                                rowGap: '8px'
                            }}
                        >
                            <Checkbox value={"Facebook"} style={{ marginLeft: 0 }}>Facebook</Checkbox>
                            <Checkbox value={"Instagram"} style={{ marginLeft: 0 }}>Instagram</Checkbox>
                            <Checkbox value={"WhatsApp"} style={{ marginLeft: 0 }}>WhatsApp</Checkbox>
                            <Checkbox value={'TikTok'} style={{ marginLeft: 0 }}>TikTok</Checkbox>
                            <Checkbox value={"YouTube"} style={{ marginLeft: 0 }}>YouTube</Checkbox>
                            <Checkbox value={"Friend/Family"} style={{ marginLeft: 0 }}>Friend/Family</Checkbox>
                            <Checkbox value={"Other"} style={{ marginLeft: 0 }}>Other</Checkbox>
                        </Checkbox.Group>
                    </Form.Item>

                    <Form.Item
                        name="discipleship"
                        label={<span style={{ fontWeight: '500', color: '#333' }}>7. Would you like to be added to our discipleship WhatsApp group or mailing list?</span>}
                        rules={[{ required: true, message: 'Please select an option' }]}
                    >
                        <Radio.Group>
                            <Radio value={"Yes"}>Yes</Radio>
                            <Radio value={"No"}>No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        name="prayer_request"
                        label={<span style={{ fontWeight: '500', color: '#333' }}>8. Any prayer request you'd like to share with us?</span>}
                    >
                        <TextArea placeholder="Enter your answer" rows={3} />
                    </Form.Item>
                </div>
            ),
        }
    ];

    if (currentStep === -1) {
        return (
            <div
                className={
                    css`
    width: 100%;
    background: white;
    height: 100%;
    `
                }
            >
                <div
                    className={css`
    width: 800px;
    max-width: 90vw;
    padding: 40px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: #ffffff;
    color: #333;
    border-radius: 20px;
    border: 1px solid rgba(0, 0, 0, 0.05);
  `}
                >
                    <div
                        className={css`
      text-align: center;
    `}
                    >
                        <div
                            className={css`
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 15px;
        border-radius: 12px;
        background: linear-gradient(135deg, #007ab8 0%, #004a7c 100%);
        box-shadow: 0 8px 20px rgba(0, 74, 124, 0.2);
        margin-top: 25px;
      `}
                        >
                            <img
                                src="/assets/images/decision_form.png"
                                alt="Decision Form"
                                className={css`
          height: 60px;
          width: auto;
        `}
                            />
                        </div>
                        <h1
                            className={css`
        margin-top: 20px;
        font-size: 2.4rem;
        font-weight: 700;
        color: #004a7c;
        letter-spacing: -0.5px;
        line-height: 1.3;
        margin-bottom: 0px;
      `}
                        >
                            Welcome to Your <br />
                            <span
                                className={css`
          background: linear-gradient(135deg, #007ab8 0%, #004a7c 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        `}
                            >
                                Spiritual Journey
                            </span>
                        </h1>
                    </div>

                    <div
                        className={css`
      padding: 30px;
      border-radius: 16px;
      margin-bottom: 30px;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      position: relative;
      overflow: hidden;
    `}
                    >


                        <p
                            className={css`
        font-size: 1.15rem;
        line-height: 1.8;
        margin-bottom: 20px;
        color: #4a5568;
        padding-left: 15px;
      `}
                        >
                            Dear Beloved Friend, We are thrilled that you've taken a moment to open this form.
                            It is no accident that you're here—God has orchestrated this divine moment to touch
                            your heart with His love and truth.
                        </p>

                        <div
                            className={css`
        background: #ffffff;
        padding: 20px;
        border-radius: 12px;
        border: 1px solid #e2e8f0;
        margin: 25px 0;
        position: relative;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
      `}
                        >
                            <div
                                className={css`
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 4px;
          background: linear-gradient(to right, #007ab8, #004a7c);
          border-radius: 12px 12px 0 0;
        `}
                            ></div>
                            <p
                                className={css`
          font-style: italic;
          margin: 0;
          font-size: 1.2rem;
          color: #2d3748;
          text-align: center;
          line-height: 1.6;
        `}
                            >
                                "whoever comes to Me, I will never cast out."
                            </p>
                            <p
                                className={css`
          font-size: 0.95rem;
          color: #718096;
          text-align: center;
          margin-top: 10px;
          font-weight: 500;
        `}
                            >
                                — John 6:37
                            </p>
                        </div>

                        <p
                            className={css`
        font-size: 1.15rem;
        line-height: 1.8;
        margin-bottom: 10px;
        color: #4a5568;
        padding-left: 15px;
      `}
                        >
                            This is your moment. This is your day. The God who formed you, knows you, and loves you
                            is calling you to Himself through His Son, Jesus Christ.
                        </p>
                    </div>

                    <div
                        className={css`
      text-align: center;
      margin-top: 40px;
    `}
                    >
                        <button
                            onClick={startForm}
                            className={css`
        background: linear-gradient(to right, #007ab8, #004a7c);
        color: #ffffff;
        border: none;
        padding: 18px 45px;
        font-size: 1.15rem;
        border-radius: 12px;
        cursor: pointer;
        box-shadow: 0 10px 25px rgba(0, 122, 184, 0.3);
        transition: all 0.3s ease;
        font-weight: 600;
        letter-spacing: 0.5px;
        position: relative;
        overflow: hidden;

        &:hover {
          transform: translateY(-3px);
          box-shadow: 0 15px 30px rgba(0, 122, 184, 0.4);
        }

        &:active {
          transform: translateY(0);
        }

        &::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            to right,
            rgba(255, 255, 255, 0.2),
            rgba(255, 255, 255, 0)
          );
        }
      `}
                        >
                            Begin Your Journey →
                        </button>

                        <p
                            className={css`
        margin-top: 25px;
        font-size: 0.95rem;
        color: #718096;
        font-weight: 500;
      `}
                        >
                            We'll guide you step by step through this process
                        </p>
                    </div>

                    <div
                        className={css`
      margin-top: 50px;
      text-align: center;
      font-size: 0.95rem;
      color: #718096;
      border-top: 1px solid #e2e8f0;
      padding-top: 25px;
    `}
                    >
                        <p>
                            Welcome to the family of God, or welcome home!{' '}
                            <span
                                className={css`
          color: #e53e3e;
        `}
                            >
                                ❤️
                            </span>
                        </p>
                    </div>
                </div>
            </div>


        );
    }

    if (currentStep === 3) {
        return (
            <div className={css`
  width: 100%;
  background: linear-gradient(135deg, #f0f4f8 0%, #d9e2ec 100%);
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -50px;
    right: -50px;
    width: 200px;
    height: 200px;
    background: rgba(0, 122, 184, 0.05);
    border-radius: 50%;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -80px;
    left: -80px;
    width: 300px;
    height: 300px;
    background: rgba(0, 74, 124, 0.05);
    border-radius: 50%;
  }
`}>
                <div className={css`
    width: 800px;
    max-width: 95vw;
    padding: 50px 40px;
    background: #ffffff;
    color: #333;
    border-radius: 24px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.1);
    margin: 40px 0;
    position: relative;
    z-index: 1;
    border: 1px solid rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(5px);
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 5px;
      background: linear-gradient(to right, #007ab8, #004a7c);
    }
  `}>
                    <div className={css`
      text-align: center;
      position: relative;
    `}>
                        <div className={css`
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        border-radius: 16px;
        background: linear-gradient(135deg, #007ab8 0%, #004a7c 100%);
        box-shadow: 0 15px 30px rgba(0, 74, 124, 0.25);
        margin-bottom: 30px;
        transform: translateY(0);
        transition: all 0.3s ease;
        position: relative;
        overflow: hidden;

        &::after {
          content: "";
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%);
          transform: rotate(30deg);
        }

        &:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 40px rgba(0, 74, 124, 0.35);
        }
      `}>
                            <img
                                src="/assets/images/decision_form.png"
                                alt="Decision Form"
                                className={css`
            height: 70px;
            width: auto;
            filter: drop-shadow(0 5px 15px rgba(0, 0, 0, 0.2));
          `}
                            />
                        </div>
                        <h1 className={css`
        margin-top: 15px;
        font-size: 1.8rem;
        font-weight: 500;
        color: #004a7c;
        letter-spacing: -0.5px;
        line-height: 1.2;
        margin-bottom: 10px;
        background: linear-gradient(to right, #007ab8, #004a7c);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        text-shadow: 0 2px 4px rgba(0, 74, 124, 0.1);
      `}>
                            Your Decision Has Been Received
                        </h1>
                        <p className={css`
        color: #64748b;
        font-size: 1.1rem;
        margin-bottom: 20px;
        max-width: 600px;
        margin-left: auto;
        margin-right: auto;
        line-height: 1.6;
      `}>
                            Thank you for taking this important step in your spiritual journey. We're praying for you!
                        </p>
                    </div>

                    <div className={css`
      text-align: center;
      margin-top: 30px;
    `}>
                        <button
                            onClick={() => setCurrentStep(-1)}
                            className={css`
          background: linear-gradient(to right, #007ab8, #004a7c);
          color: #ffffff;
          border: none;
          padding: 12px 45px;
          font-size: 1rem;
          border-radius: 14px;
          cursor: pointer;
          box-shadow: 0 15px 30px rgba(0, 122, 184, 0.3);
          transition: all 0.3s ease;
          letter-spacing: 0.5px;
          position: relative;
          overflow: hidden;
          display: inline-flex;
          align-items: center;
          gap: 10px;

          &::after {
            content: "";
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
            transition: 0.5s;
          }

          &:hover {
            transform: translateY(-5px);
            box-shadow: 0 20px 40px rgba(0, 122, 184, 0.4);

            &::after {
              left: 100%;
            }
          }

          &:active {
            transform: translateY(-2px);
          }
        `}
                        >
                            Start Again <span className={css`transition: transform 0.3s ease;`}>→</span>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={css`
      line-height: 1.6;
      color: #333;
      margin: 0;
      display: flex;
      justify-content: center;
      align-items: flex-start;
      min-height: 100vh;
      background: linear-gradient(135deg, #f6f9fc 0%, #e3f2fd 100%);
      box-sizing: border-box;
      height: 100%;
      overflow-y: scroll;
      padding: 20px 0;
      opacity: ${showForm ? 1 : 0};
      transition: opacity 0.5s ease;
    `}>
            <div className={css`
        background-color: #fff;
        border-radius: 20px;
        box-shadow: 
          0 15px 35px rgba(50, 50, 93, 0.1),
          0 5px 15px rgba(0, 0, 0, 0.07);
        width: 100%;
        max-width: 1040px;
        margin: 0 20px;
        overflow: hidden;
        transform: translateY(${showForm ? '0' : '20px'});
        transition: all 0.5s cubic-bezier(0.23, 1, 0.32, 1);
        animation: ${fadeIn} 0.6s ease-out;
      `}>
                <div className={css`
          background: linear-gradient(135deg, #0088cc 0%, #006699 100%);
          color: #fff;
          padding: 50px 40px;
          text-align: center;
          position: relative;
          overflow: hidden;
          
          &::before {
            content: '';
            position: absolute;
            top: -50%;
            right: -50%;
            width: 200%;
            height: 200%;
            background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
            transform: rotate(30deg);
          }
          
          &::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 8px;
            background: linear-gradient(90deg, 
              rgba(255,255,255,0.3) 0%, 
              rgba(255,255,255,0.8) 50%, 
              rgba(255,255,255,0.3) 100%);
          }
        `}>
                    <h1 className={css`
            font-size: 2rem;
            margin-bottom: 15px;
            color: white;
            font-weight: 600;
            text-shadow: 0 2px 4px rgba(0,0,0,0.1);
            position: relative;
            z-index: 1;
          `}>
                        Your Spiritual Journey
                    </h1>
                    <p className={css`
            font-size: 1rem;
            max-width: 800px;
            margin: 0 auto 20px;
            line-height: 1.7;
            opacity: 0.9;
            position: relative;
            z-index: 1;
          `}>
                        Let's walk together in faith. Complete this form to begin your spiritual growth.
                    </p>
                </div>

                <div className={css`
          padding: 30px 40px;
          @media (max-width: 768px) {
            padding: 30px 20px;
          }
        `}>
                    <Steps
                        current={currentStep}
                        className={css`
              margin-bottom: 20px;
              
              .ant-steps-item-title {
                font-weight: 500;
                color: #555;
              }
              
              .ant-steps-item-process .ant-steps-item-title {
                color: #0088cc;
                font-weight: 600;
              }
            `}
                    >
                        {steps.map((item) => (
                            <Steps.Step
                                key={item.title}
                                title={item.title}
                                className={css`
                  .ant-steps-icon {
                    font-size: 16px;
                  }
                `}
                            />
                        ))}
                    </Steps>

                    <Form
                        form={form}
                        name="decisionForm"
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                        layout="vertical"
                        onValuesChange={valueChangeHandler}
                        className={css`
              .ant-form-item-label > label {
                font-weight: 500;
                color: #555;
              }
            `}
                    >
                        <div className={css`
              padding: 20px 0;
              min-height: 300px;
            `}>
                            {steps[currentStep].content}
                        </div>

                        <div className={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
            `}>
                            {currentStep > 0 && (
                                <Button
                                    onClick={prevStep}
                                    className={css`
                    padding: 0 28px;
                    height: 40px;
                    background: #f8f9fa;
                    border-color: #e9ecef;
                    border-radius: 12px;
                    font-size: 15px;
                    display: flex;
                    align-items: center;
                    transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
                    
                    &:hover {
                      background: #e9ecef;
                      border-color: #dee2e6;
                      transform: translateX(-3px);
                    }
                  `}
                                >
                                    <ArrowLeftOutlined /> Previous
                                </Button>
                            )}
                            {currentStep < steps.length - 1 && (
                                <Button
                                    type="primary"
                                    onClick={nextStep}
                                    className={css`
                    padding: 0 28px;
                    height: 40px;
                    background: linear-gradient(135deg, #0088cc 0%, #006699 100%);
                    border: none;
                    margin-left: auto;
                    border-radius: 12px;
                    font-size: 15px;
                    display: flex;
                    align-items: center;
                    transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
                    box-shadow: 0 4px 15px rgba(0, 136, 204, 0.3);
                    
                    &:hover {
                      transform: translateY(-2px);
                      box-shadow: 0 6px 20px rgba(0, 136, 204, 0.4);
                    }
                    
                    &:active {
                      transform: translateY(0);
                    }
                  `}
                                >
                                    Next <ArrowRightOutlined />
                                </Button>
                            )}
                            {currentStep === steps.length - 1 && (
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    icon={<SendOutlined />}
                                    className={css`
                    padding: 0 28px;
                    height: 40px;
                    background: linear-gradient(135deg, #0088cc 0%, #006699 100%);
                    border: none;
                    margin-left: auto;
                    border-radius: 12px;
                    font-size: 15px;
                    display: flex;
                    align-items: center;
                    transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
                    box-shadow: 0 4px 15px rgba(0, 204, 136, 0.3);
                    
                    &:hover {
                      transform: translateY(-2px);
                      box-shadow: 0 6px 20px rgba(0, 204, 136, 0.4);
                      animation: none;
                    }
                    
                    &:active {
                      transform: translateY(0);
                    }
                  `}
                                    loading={isLoading}
                                    disabled={isLoading}
                                >
                                    Submit Your Decision
                                </Button>
                            )}
                        </div>
                    </Form>
                </div>
            </div>
        </div>
    );
};

export default DecisionForm;